import React, {useCallback} from 'react';

import {WidthItem} from 'src/components/width-item';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {dateToDateString} from 'src/hooks/date-time/utils';
import {sortNewsletterItems} from 'src/utils/sortNewsletterItems';

import {Calendar} from '@pb/kit-component-calendar';
import ComboBox, {ComboboxGroup, ComboboxItem} from '@pb/kit-component-combo-box';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import type {CalendarProps} from '@pb/kit-component-calendar';
import type {DictionaryTree} from 'src/openapi/admin/models/DictionaryTree';
import type {NewsletterHistoryFilters} from 'src/openapi/admin/models/NewsletterHistoryFilters';

type Props = {
    filter?: NewsletterHistoryFilters,
    onChange?: (filters: NewsletterHistoryFilters) => void,
    newsletterDictionary: DictionaryTree[],
};
export const NewsletterHistoryFiltersBody = ({filter, onChange, newsletterDictionary}: Props) => {
    const {getLocalDate} = useDateTime();


    const changeNewsletterTypes = useCallback((newsletterTypes: string[]) => {
        if (filter && onChange) {
            onChange({
                ...filter,
                newsletterTypes,
            });
        }
    }, [filter, onChange]);
    const changeHistoryPeriod = useCallback<NonNullable<CalendarProps['onChangeRange']>>(([start, end]) => {
        if (filter && onChange) {
            onChange({
                ...filter,
                period: {
                    startDate: start ? dateToDateString(start) : undefined,
                    endDate: end ? dateToDateString(end) : undefined,
                },
            });
        }
    }, [filter, onChange]);

    return (
        <Group direction="v">
            <WidthItem width={250}>
                <Label>Newsletter</Label>
                <ComboBox
                    multiselectable={true}
                    placeholder="Select newsletter"
                    value={filter?.newsletterTypes}
                    onChange={changeNewsletterTypes}>
                    {newsletterDictionary.flatMap((item, index, arr) => {
                        const comboboxGroupIndex = arr.findIndex(i => (i.children?.length ?? 0) > 1);
                        if (item.children?.length && index === comboboxGroupIndex) {
                            const sorted = sortNewsletterItems(item.children);
                            return [(
                                <ComboboxGroup key={item.id} caption={item.description}>
                                    {sorted.map(child => (
                                        <ComboboxItem key={child.id} value={child.id} caption={child.description}/>
                                    ))}
                                </ComboboxGroup>
                            )];
                        }
                        if (item.children?.length) {
                            const sorted = sortNewsletterItems(item.children);
                            return sorted.map(child => (
                                <ComboboxItem key={child.id} value={child.id} caption={child.description}/>
                            ));
                        }
                        return [<ComboboxItem key={item.id} value={item.id} caption={item.description}/>];
                    })}
                </ComboBox>
            </WidthItem>
            <div>
                <Label>Select a period</Label>
                <Calendar {...{
                    inline: true,
                    selectsRange: true,
                    startDate: filter?.period?.startDate
                        ? getLocalDate(filter.period.startDate)
                        : undefined,
                    endDate: filter?.period?.endDate
                        ? getLocalDate(filter.period.endDate)
                        : undefined,
                    onChangeRange: changeHistoryPeriod,
                }}
                />
            </div>
        </Group>
    );
};
